import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Check, ChevronDown, Search } from "lucide-react";
import { Input } from "campaign-builder/ui";
import { getAdmFormatList, getAdmFormatMeta } from "./formatMeta";

interface SelectedFormatRowProps {
  value?: string | null;
  allowed?: string[];
  disabled?: boolean;
  onSelect: (format: string) => void;
}

// Per-format postcard thumbnail sizes from Figma (Frame 47536), oriented landscape
// (width = longer side) per the Product-confirmed landscape treatment.
const THUMB_SIZE: Record<string, { w: number; h: number }> = {
  POSTCARD_6x4: { w: 26, h: 16 },
  POSTCARD_9x6: { w: 35.4, h: 24.58 },
  POSTCARD_11x6: { w: 43.27, h: 24.58 },
  POSTCARD_15x12: { w: 48, h: 38.4 },
};

const FormatThumb = ({ format }: { format?: string }) => {
  const size = (format && THUMB_SIZE[format]) || THUMB_SIZE.POSTCARD_9x6;
  return (
    <div className="flex h-12 w-[60px] shrink-0 items-center justify-center">
      <div
        className="flex items-center justify-center rounded border-[0.5px] border-solid border-[#62748e] bg-white p-[2px]"
        style={{ width: size.w, height: size.h }}
      >
        <div className="h-full w-full rounded-[3px] bg-[#e2e8f0]" />
      </div>
    </div>
  );
};

const BestSellerBadge = () => (
  <span className="rounded-full bg-[#dcfce7] px-2 py-0.5 text-xs font-medium text-[#008236]">Best seller</span>
);

export const SelectedFormatRow: React.FC<SelectedFormatRowProps> = ({ value, allowed, disabled, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const selected = getAdmFormatMeta(value);
  const options = getAdmFormatList(allowed).filter((meta) => meta.label.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (format: string) => {
    setOpen(false);
    setQuery("");
    if (format !== value) onSelect(format);
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-foreground text-sm font-medium leading-5">Selected format</p>

      <div ref={ref} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          data-cy="admDesignFormatChangeButton"
          className="border-border flex w-full items-center gap-4 rounded-lg border border-solid bg-background p-4 disabled:opacity-60"
        >
          {selected ? (
            <>
              <FormatThumb format={selected.value} />
              <div className="flex flex-1 flex-col items-start gap-1 overflow-hidden">
                <div className="flex items-center gap-2">
                  <span className="text-foreground text-sm font-medium leading-5">{selected.label}</span>
                  {selected.isBestSeller && <BestSellerBadge />}
                </div>
                <span className="text-muted-foreground text-xs leading-4">
                  {selected.sizeLabel} · {selected.price}
                </span>
              </div>
            </>
          ) : (
            <span className="text-muted-foreground flex-1 text-left text-sm">Select a format</span>
          )}
          <ChevronDown className={cn("text-muted-foreground h-5 w-5 transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div className="border-border absolute left-0 right-0 top-full z-30 mt-2 flex flex-col gap-2 rounded-lg border border-solid bg-card p-2 shadow-[0px_8px_24px_rgba(2,6,23,0.12)]">
            <div className="relative">
              <Search className="text-slate-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search format..."
                className="pl-9"
              />
            </div>
            <div className="flex flex-col">
              {options.map((meta) => {
                const isSelected = meta.value === value;
                return (
                  <button
                    key={meta.value}
                    type="button"
                    onClick={() => choose(meta.value)}
                    data-cy={`admDesignFormatOption-${meta.value}`}
                    className={cn(
                      "flex items-center gap-3 rounded-lg p-3 text-left",
                      isSelected ? "bg-slate-50 dark:bg-muted" : "hover:bg-slate-50 dark:hover:bg-muted"
                    )}
                  >
                    <FormatThumb format={meta.value} />
                    <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground text-sm font-medium leading-5">{meta.label}</span>
                        {meta.isBestSeller && <BestSellerBadge />}
                      </div>
                      <span className="text-muted-foreground text-xs leading-4">
                        {meta.sizeLabel} · {meta.price}
                      </span>
                    </div>
                    {isSelected && <Check className="text-muted-foreground h-5 w-5" strokeWidth={1.5} />}
                  </button>
                );
              })}
              {options.length === 0 && (
                <p className="text-muted-foreground p-3 text-sm">No formats match your search.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
